import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { State } from "@feature/invoice-status/reducers/invoice-status-widget";
import * as invoiceStatusWidgetSelectors from "@feature/invoice-status/selectors/invoice-status-widget";
import { Observable } from "rxjs";
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
} from "rxjs";
import { WidgetAndConfigId } from "@common/models/widget-and-config-id";
import * as filterSelectors from "@common/filter/selectors/filter.selectors";
import { CurrencyService } from "@currency/services/currency.service";
import { Currency } from "@currency/models/currency.model";
import { InvoiceStatusWidgetHttpService } from "@feature/invoice-status/services/invoice-status-widget-http.service";
import {ITable} from "@feature/common";

@Injectable({
  providedIn: "root",
})
export class InvoiceStatusWidgetFacade {
  private allCurrencies: Observable<Currency[]>;
  private cacheTimeout = 10000;

  constructor(
    private readonly invoiceStatusHttp: InvoiceStatusWidgetHttpService,
    private readonly currencyService: CurrencyService,
    private readonly store: Store<State>,
  ) {
  }

  private getAllCurrencies() {
    if (!this.allCurrencies) {
      setTimeout(this.clearCache, this.cacheTimeout);
      this.allCurrencies = this.currencyService.getCurrencies()
        .pipe(
          shareReplay(1),
        );
    }
    return this.allCurrencies;
  }

  private clearCache(): void {
    this.allCurrencies = null;
  }

  widgetLoading(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(invoiceStatusWidgetSelectors.getWidgetLoading(widget.id)),
    ).pipe(distinctUntilChanged());
  }

  currentView(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(invoiceStatusWidgetSelectors.getCurrentView(widget.id)),
    ).pipe(distinctUntilChanged());
  }

  grossAmountByStatus(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(
        invoiceStatusWidgetSelectors.grossAmountByStatus(
          widget.id,
          widget.widgetConfigId,
        ),
      ),
      filter((d) => d != null),
    );
  }

  volumeByStatus(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(
        invoiceStatusWidgetSelectors.volumeByStatus(
          widget.id,
          widget.widgetConfigId,
        ),
      ),
      filter((d) => d != null),
    );
  }

  statusTable(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(
        invoiceStatusWidgetSelectors.tableData(
          widget.id,
          widget.widgetConfigId,
        ),
      ),
      filter((d) => d != undefined),
      distinctUntilChanged(),
    );
  }

  queryBuilderConfig(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(invoiceStatusWidgetSelectors.queryBuilderConfig(widget.id)),
    ).pipe(distinctUntilChanged());
  }

  userFilter(widget: WidgetAndConfigId) {
    return this.store.pipe(select(filterSelectors.getWidgetFilter(widget.id))).pipe(
      distinctUntilChanged(),
    );
  }

  currency(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(invoiceStatusWidgetSelectors.getCurrency(widget.id)),
    ).pipe(distinctUntilChanged());
  }

  availableCurrencies(widget: WidgetAndConfigId) {
    const currencySource = combineLatest([
      this.store.pipe(
        select(invoiceStatusWidgetSelectors.availableCurrencies(widget.id)),
      ),
      this.getAllCurrencies(),
    ]);
    return currencySource.pipe(
      map(([available, all]) => {
        return all.filter((item) => {
          return available[item.Code] !== undefined;
        });
      }),
    );
  }

  isInDrillDown(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(invoiceStatusWidgetSelectors.getDrillDownActive(widget.id)),
      distinctUntilChanged(),
    );
  }

  drillDownLabel(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(invoiceStatusWidgetSelectors.getDrillDownLabel(widget.id)),
      distinctUntilChanged(),
    );
  }

  widgetConfiguration(widget: WidgetAndConfigId) {
    return this.invoiceStatusHttp.getWidgetConfiguration(widget.widgetConfigId);
  }

  getItemLevelData(widgetInstanceId: number, payablesRegisterUri: number, external: boolean): Observable<ITable> {
    return this.invoiceStatusHttp.getItemLevelData(widgetInstanceId, payablesRegisterUri, external);
  }
}
